import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import momentTZ from "moment-timezone";
import { useParams } from "react-router-dom";
import { useFormContext } from "react-hook-form";

import { useGetCountries } from "api/queries/common/useGetCountries";
import { useGetQuoteRequestDetails } from "api/queries/quotes/useGetQuoteRequestDetails";

import TimeInput from "components/atoms/TimeInput/TimeInput";
import Alert from "components/molecules/Alert/Alert";
import PickupDeliveryDate from "pages/shipper/RequestForm/components/Schedule/PickupDeliveryDate";
import EditScheduleStopInfo from "./EditScheduleStopInfo";

import { IEditScheduleForm } from "../types";

const mergeStopForDisplay = (formStop: any, quoteStop: any) => {
  if (!quoteStop) return formStop;
  return {
    ...quoteStop,
    ...formStop,
    address_details: formStop?.address_details ?? quoteStop?.address_details,
    stop_type: formStop?.stop_type ?? quoteStop?.stop_type,
    stop_after_datetime: formStop?.stop_after_datetime ?? quoteStop?.stop_after_datetime,
    stop_before_datetime: formStop?.stop_before_datetime ?? quoteStop?.stop_before_datetime,
    stop_time_type: formStop?.stop_time_type ?? quoteStop?.stop_time_type,
    stop_timezone: formStop?.stop_timezone ?? quoteStop?.stop_timezone,
  };
};

export default function EditScheduleMultistopStopsForm() {
  const id = useParams().id?.toUpperCase()!;
  const [holidays, setHolidays] = useState({});
  const { data: quoteRequestData } = useGetQuoteRequestDetails({
    shipmentId: id,
  });
  const quoteStops = quoteRequestData?.schedule?.stops || [];

  const {
    watch,
    getValues,
    trigger,
    formState: { errors },
  } = useFormContext<IEditScheduleForm>();
  const formStops = watch("stops") || [];

  const { data: countries = [] } = useGetCountries({
    refetchOnMount: false,
  });

  const displayStops = useMemo(
    () => formStops.map((s: any, i: number) => mergeStopForDisplay(s, quoteStops[i])),
    [formStops, quoteStops],
  );
  const firstPickupIndex = useMemo(() => displayStops.findIndex((s: any) => s.stop_type === "Pickup"), [displayStops]);
  const firstPickupDate = firstPickupIndex >= 0 ? formStops[firstPickupIndex]?.updated_date : undefined;
  const firstPickupTime = firstPickupIndex >= 0 ? formStops[firstPickupIndex]?.updated_time : undefined;
  const deliveryStopIndexes = useMemo(
    () =>
      displayStops.reduce<number[]>((acc, s: any, idx: number) => {
        if (s.stop_type !== "Pickup") acc.push(idx);
        return acc;
      }, []),
    [displayStops],
  );

  useEffect(() => {
    if (firstPickupIndex < 0 || !deliveryStopIndexes.length) return;
    trigger(deliveryStopIndexes.map((idx) => `stops.${idx}.updated_date` as const)).catch(() => {});
  }, [firstPickupDate, firstPickupTime, firstPickupIndex, deliveryStopIndexes, trigger]);

  const getFirstPickupBaseline = useCallback(() => {
    if (firstPickupIndex < 0) return null;
    const pickupStop = displayStops[firstPickupIndex];
    const pickupDate = getValues(`stops.${firstPickupIndex}.updated_date`);
    const pickupTime = getValues(`stops.${firstPickupIndex}.updated_time`) || "00:00:00";
    const pickupTimezone = pickupStop?.stop_timezone;
    if (!pickupDate || !pickupTimezone) return null;

    const pickupDatetime = momentTZ.tz(`${pickupDate} ${pickupTime}`, "YYYY-MM-DD HH:mm:ss", pickupTimezone);
    if (!pickupDatetime.isValid()) return null;

    return {
      index: firstPickupIndex,
      datetime: pickupDatetime,
      timezone: pickupTimezone,
      updated_date: pickupDate,
      updated_time: pickupTime,
    };
  }, [displayStops, firstPickupIndex, getValues]);

  const formattedEarliestPickup = useMemo(() => {
    const baseline = getFirstPickupBaseline();
    return baseline?.datetime.format("DD MMMM YYYY. HH:mm") ?? "";
  }, [getFirstPickupBaseline, firstPickupDate, firstPickupTime]);

  const checkValidDeliveryDate = useCallback(
    (index: number) => {
      const firstPickupBaseline = getFirstPickupBaseline();
      if (!firstPickupBaseline) return true;
      const date = formStops[index]?.updated_date;
      const time = formStops[index]?.updated_time || "00:00:00";
      const stopTz = displayStops[index]?.stop_timezone;
      if (!date || !stopTz) return true;
      const deliveryMoment = momentTZ.tz(`${date} ${time}`, "YYYY-MM-DD HH:mm:ss", stopTz);
      if (!deliveryMoment.isValid()) return true;
      const isAfterPickup = deliveryMoment.isAfter(firstPickupBaseline.datetime);
      return isAfterPickup;
    },
    [getFirstPickupBaseline, formStops, displayStops, firstPickupIndex],
  );

  return (
    <div className="stop-schedule-form">
      {displayStops.map((stop: any, index: number) => {
        const isPickup = stop.stop_type === "Pickup";
        const currentDate = getValues(`stops.${index}.updated_date`);
        const currentTime = getValues(`stops.${index}.updated_time`);
        const rowKey = stop.id != null ? String(stop.id) : `stop-${index}`;

        return (
          <div key={rowKey}>
            <div className="flex-row mb-24">
              <div className="w-300 pr-16">
                <EditScheduleStopInfo stop={stop} />
              </div>

              <div className="flex-grow-1">
                <div className="flex-row">
                  <div className="w-192 align-date">
                    <label className="required font-b3 mb-1 d-block">
                      {isPickup ? (
                        <FormattedMessage
                          id="tracking.components.editScheduleDialog.pickupDate"
                          defaultMessage="Pickup date"
                        />
                      ) : (
                        <FormattedMessage
                          id="tracking.components.editScheduleDialog.estimatedDeliveryDate"
                          defaultMessage="Estimated Delivery Date"
                        />
                      )}
                    </label>
                    <PickupDeliveryDate
                      datePickerProps={{
                        name: `stops.${index}.updated_date`,
                        required: true,
                        defaultValue: currentDate,
                        ...(!isPickup &&
                          firstPickupIndex >= 0 && {
                            validations: {
                              isValidDeliveryDate: () => checkValidDeliveryDate(index),
                            },
                            registerOptions:
                              firstPickupIndex >= 0
                                ? {
                                    deps: [
                                      `stops.${index}.updated_time`,
                                      `stops.${firstPickupIndex}.updated_date`,
                                      `stops.${firstPickupIndex}.updated_time`,
                                    ],
                                  }
                                : undefined,
                            hideValidationError: true,
                          }),
                      }}
                      countries={countries}
                      holidays={holidays}
                      setHolidays={setHolidays}
                    />
                  </div>

                  <div className="w-192">
                    <label className="required font-b3 mb-1 d-block">
                      {isPickup ? (
                        <FormattedMessage
                          id="tracking.components.editScheduleDialog.pickupTime"
                          defaultMessage="Pickup Time"
                        />
                      ) : (
                        <FormattedMessage
                          id="tracking.components.editScheduleDialog.estimatedDeliveryTime"
                          defaultMessage="Estimated Delivery Time"
                        />
                      )}
                    </label>
                    <TimeInput
                      name={`stops.${index}.updated_time`}
                      required
                      defaultValue={currentTime}
                      {...(!isPickup && {
                        registerOptions: {
                          deps: [`stops.${index}.updated_date`],
                        },
                      })}
                    />
                  </div>
                </div>

                {!isPickup && (errors as any)?.stops?.[index]?.updated_date && (
                  <>
                    {(errors as any).stops[index].updated_date.type === "required" ? (
                      <div className="d-block font-b3 invalid-feedback">
                        <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
                      </div>
                    ) : (
                      <Alert className="w-100 mt-md" variant="danger">
                        <FormattedMessage
                          id="new-request.schedule.multistop.error.deliveryAfterPickup"
                          defaultMessage="Delivery schedule should be after {pickupDateTime}"
                          values={{ pickupDateTime: formattedEarliestPickup }}
                        />
                      </Alert>
                    )}
                  </>
                )}
              </div>
            </div>

            {index < displayStops.length - 1 && <hr />}
          </div>
        );
      })}
    </div>
  );
}
